import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

import { FOCUS_RING_INSET } from "../lib/focus";
import { cn } from "../lib/utils";

/**
 * v2 ScrollArea (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap. Same names
 * as v1 (`ScrollArea`, `ScrollBar`), so the swap is a move; the add-in fork
 * merges into it.
 *
 * For panes inside the app frame (lists, panels, an e-mail body). Page,
 * dialog and overlay content scroll natively. The bar overlays the content and
 * shows while the pointer is in the pane (`type="hover"`, as v1), so nothing
 * shifts when it appears: no `scrollbar-gutter` needed here.
 *
 * Content column (break #128): Radix wraps the content in a `display: table`
 * div, which lets wide content stretch the pane. Six web-app sites forced it
 * to `block` and the add-in forked the file to make it a column. With
 * `scrollbars="vertical"` (default) the wrapper is a full-width column at
 * least as tall as the pane; `horizontal` / `both` keep Radix's table, which
 * is what sizes wide content, and render the horizontal bar.
 *
 * Look: an 8px track, a 6px `--input` thumb (3.31:1, readable whenever it
 * shows; `--border` was 1.5:1), `--ink-muted` on hover and drag.
 *
 * `viewportLabel` makes the viewport a focusable, named region for read-only
 * content without focusable children, so keyboard users can scroll it.
 * `data-disable-scroll` keeps v1's pass-through mode (an ancestor scrolls).
 */

export type ScrollAreaScrollbars = "vertical" | "horizontal" | "both";

export interface ScrollAreaProps extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  scrollbars?: ScrollAreaScrollbars;
  /** The scrolling element (for scroll-to-bottom etc.). */
  viewportRef?: React.Ref<HTMLDivElement>;
  /** Name of the pane; makes the viewport a focusable region. German UI text. */
  viewportLabel?: string;
}

const ScrollArea = React.forwardRef<React.ElementRef<typeof ScrollAreaPrimitive.Root>, ScrollAreaProps>(
  function ScrollArea({ scrollbars = "vertical", viewportRef, viewportLabel, className, children, ...props }, ref) {
    // v1 contract: with `data-disable-scroll` an ancestor owns scrolling; a forced
    // full-height viewport would collapse to 0 inside a height:auto layout.
    const disableScroll = Boolean((props as Record<string, unknown>)["data-disable-scroll"]);
    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        className={cn("relative", !disableScroll && "overflow-hidden", className)}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          ref={viewportRef}
          tabIndex={viewportLabel ? 0 : undefined}
          role={viewportLabel ? "region" : undefined}
          aria-label={viewportLabel}
          className={cn(
            "w-full outline-hidden",
            !disableScroll && "h-full",
            // Radix sets `display: table` inline; important beats it.
            scrollbars === "vertical" && "*:flex! *:min-h-full! *:flex-col!",
            FOCUS_RING_INSET,
          )}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        {scrollbars !== "horizontal" && <ScrollBar />}
        {scrollbars !== "vertical" && <ScrollBar orientation="horizontal" />}
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  },
);

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(function ScrollBar({ className, orientation = "vertical", ...props }, ref) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      ref={ref}
      orientation={orientation}
      className={cn(
        "flex touch-none select-none p-px",
        orientation === "vertical" ? "h-full w-2" : "h-2 flex-col",
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-sm bg-input transition-colors hover:bg-ink-muted active:bg-ink-muted" />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
});

export { ScrollArea, ScrollBar };
